import { createBrowserRouter, Navigate, Outlet } from 'react-router'
import { lazy, Suspense } from 'react'
import { AuthLayout } from '@/layouts/auth-layout'
import { AdminLayout } from '@/layouts/admin-layout'
import { PortalLayout } from '@/layouts/portal-layout'
import { ProtectedRoute } from '@/components/shared/protected-route'
import { LoadingState } from '@/components/shared/loading-state'
import { AuthProvider } from '@/providers/auth-provider'
import {
  AppErrorBoundary,
  LayoutErrorBoundary,
  FeatureErrorBoundary,
} from '@/components/shared/error-boundary'

function RootLayout() {
  return (
    <AuthProvider>
      <Outlet />
    </AuthProvider>
  )
}

// Auth pages — fully implemented by Builder 2
import LoginPage from '@/pages/auth/login'
import PasswordSetupPage from '@/pages/auth/password-setup'

// Active rebuild slice: dashboard foundation only.
const AdminDashboard = lazy(() => import('@/pages/admin/dashboard'))
const AdminClients = lazy(() => import('@/pages/admin/clients'))
const AdminOnboard = lazy(() => import('@/pages/admin/onboard'))
const AdminApprovals = lazy(() => import('@/pages/admin/approvals'))
const AdminFiles = lazy(() => import('@/pages/admin/files'))
const AdminActivity = lazy(() => import('@/pages/admin/activity'))
const AdminInvoices = lazy(() => import('@/pages/admin/invoices'))
const AdminAnalytics = lazy(() => import('@/pages/admin/analytics'))
const AdminChangeRequests = lazy(() => import('@/pages/admin/change-requests'))
const AdminNewsletters = lazy(() => import('@/pages/admin/newsletters'))
const AdminAiBlog = lazy(() => import('@/pages/admin/ai-blog'))
const AdminBlog = lazy(() => import('@/pages/admin/blog'))
const AdminModeration = lazy(() => import('@/pages/admin/moderation'))
const AdminPerformance = lazy(() => import('@/pages/admin/performance'))
const AdminSecurity = lazy(() => import('@/pages/admin/security'))
const AdminOpsBrain = lazy(() => import('@/pages/admin/ops-brain'))
const AdminSystems = lazy(() => import('@/pages/admin/systems'))
const AdminGithub = lazy(() => import('@/pages/admin/github'))
const AdminManagedSites = lazy(() => import('@/pages/admin/sites'))
const AdminManagedSiteDetail = lazy(() => import('@/pages/admin/site-detail'))
const PortalDashboard = lazy(() => import('@/pages/portal/dashboard'))
const PortalInsights = lazy(() => import('@/pages/portal/insights'))
const PortalPublishing = lazy(() => import('@/pages/portal/publishing'))
const PortalNewsletter = lazy(() => import('@/pages/portal/newsletter'))
const PortalBlog = lazy(() => import('@/pages/portal/blog'))
const PortalBookings = lazy(() => import('@/pages/portal/bookings'))
const PortalChanges = lazy(() => import('@/pages/portal/changes'))
const PortalReferrals = lazy(() => import('@/pages/portal/referrals'))
const PortalInvoices = lazy(() => import('@/pages/portal/invoices'))
const PortalResources = lazy(() => import('@/pages/portal/resources'))
const PortalCommunity = lazy(() => import('@/pages/portal/community-hub'))
const PortalPerks = lazy(() => import('@/pages/portal/perks'))
const PortalRecentActivity = lazy(() => import('@/pages/portal/alerts'))
const PortalSettings = lazy(() => import('@/pages/portal/settings'))
const PortalVault = lazy(() => import('@/pages/portal/vault'))
const PortalFaq = lazy(() => import('@/pages/portal/faq'))
const PortalPrivacy = lazy(() => import('@/pages/portal/privacy'))
const PortalTerms = lazy(() => import('@/pages/portal/terms'))
const PortalSecurity = lazy(() => import('@/pages/portal/security-info'))

const NotFound = lazy(() => import('@/pages/not-found'))

/**
 * LazyPage wraps each lazy-loaded page in Suspense + FeatureErrorBoundary.
 * This means one broken page can't take down the whole app — the layout
 * (sidebar, header) stays functional while only the content area errors.
 */
function LazyPage({ children }: { children: React.ReactNode }) {
  return (
    <Suspense fallback={<LoadingState variant="page" />}>
      <FeatureErrorBoundary>{children}</FeatureErrorBoundary>
    </Suspense>
  )
}

export const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
  // ── Auth routes (no sidebar) ──────────────────────────────────────────
  {
    element: <AuthLayout />,
    children: [
      { path: '/login', element: <LoginPage /> },
      { path: '/setup-password', element: <PasswordSetupPage /> },
    ],
  },

  // ── Admin routes ──────────────────────────────────────────────────────
  {
    element: (
      <AppErrorBoundary>
        <ProtectedRoute requiredType="admin">
          <LayoutErrorBoundary>
            <AdminLayout />
          </LayoutErrorBoundary>
        </ProtectedRoute>
      </AppErrorBoundary>
    ),
    children: [
      { path: '/admin', element: <LazyPage><AdminDashboard /></LazyPage> },
      { path: '/admin/clients', element: <LazyPage><AdminClients /></LazyPage> },
      { path: '/admin/onboard', element: <LazyPage><AdminOnboard /></LazyPage> },
      { path: '/admin/approvals', element: <LazyPage><AdminApprovals /></LazyPage> },
      { path: '/admin/files', element: <LazyPage><AdminFiles /></LazyPage> },
      { path: '/admin/activity', element: <LazyPage><AdminActivity /></LazyPage> },
      { path: '/admin/invoices', element: <LazyPage><AdminInvoices /></LazyPage> },
      { path: '/admin/analytics', element: <LazyPage><AdminAnalytics /></LazyPage> },
      { path: '/admin/change-requests', element: <LazyPage><AdminChangeRequests /></LazyPage> },
      { path: '/admin/newsletters', element: <LazyPage><AdminNewsletters /></LazyPage> },
      { path: '/admin/ai-blog', element: <LazyPage><AdminAiBlog /></LazyPage> },
      { path: '/admin/blog', element: <LazyPage><AdminBlog /></LazyPage> },
      { path: '/admin/moderation', element: <LazyPage><AdminModeration /></LazyPage> },
      { path: '/admin/performance', element: <LazyPage><AdminPerformance /></LazyPage> },
      { path: '/admin/security', element: <LazyPage><AdminSecurity /></LazyPage> },
      { path: '/admin/ops-brain', element: <LazyPage><AdminOpsBrain /></LazyPage> },
      { path: '/admin/systems', element: <LazyPage><AdminSystems /></LazyPage> },
      { path: '/admin/github', element: <LazyPage><AdminGithub /></LazyPage> },
      { path: '/admin/sites', element: <LazyPage><AdminManagedSites /></LazyPage> },
      { path: '/admin/sites/:siteId', element: <LazyPage><AdminManagedSiteDetail /></LazyPage> },
    ],
  },

  // ── Portal routes ─────────────────────────────────────────────────────
  {
    element: (
      <AppErrorBoundary>
        <ProtectedRoute requiredType="client">
          <LayoutErrorBoundary>
            <PortalLayout />
          </LayoutErrorBoundary>
        </ProtectedRoute>
      </AppErrorBoundary>
    ),
    children: [
      { path: '/portal', element: <LazyPage><PortalDashboard /></LazyPage> },
      { path: '/portal/insights', element: <LazyPage><PortalInsights /></LazyPage> },
      { path: '/portal/publishing', element: <LazyPage><PortalPublishing /></LazyPage> },
      { path: '/portal/newsletter', element: <LazyPage><PortalNewsletter /></LazyPage> },
      { path: '/portal/blog', element: <LazyPage><PortalBlog /></LazyPage> },
      { path: '/portal/bookings', element: <LazyPage><PortalBookings /></LazyPage> },
      { path: '/portal/changes', element: <LazyPage><PortalChanges /></LazyPage> },
      { path: '/portal/referrals', element: <LazyPage><PortalReferrals /></LazyPage> },
      { path: '/portal/invoices', element: <LazyPage><PortalInvoices /></LazyPage> },
      { path: '/portal/resources', element: <LazyPage><PortalResources /></LazyPage> },
      { path: '/portal/community', element: <LazyPage><PortalCommunity /></LazyPage> },
      { path: '/portal/perks', element: <LazyPage><PortalPerks /></LazyPage> },
      { path: '/portal/alerts', element: <LazyPage><PortalRecentActivity /></LazyPage> },
      { path: '/portal/settings', element: <LazyPage><PortalSettings /></LazyPage> },
      { path: '/portal/vault', element: <LazyPage><PortalVault /></LazyPage> },
      { path: '/portal/faq', element: <LazyPage><PortalFaq /></LazyPage> },
      { path: '/portal/privacy', element: <LazyPage><PortalPrivacy /></LazyPage> },
      { path: '/portal/terms', element: <LazyPage><PortalTerms /></LazyPage> },
      { path: '/portal/security', element: <LazyPage><PortalSecurity /></LazyPage> },
    ],
  },

  // ── Root redirect ─────────────────────────────────────────────────────
  { path: '/', element: <Navigate to="/login" replace /> },

  // ── 404 ───────────────────────────────────────────────────────────────
  { path: '*', element: <LazyPage><NotFound /></LazyPage> },
  ], // end RootLayout children
  }, // end RootLayout route
])
